import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  api,
  formatInr,
  type BalanceRow,
  type BalanceSheet,
  type Location,
  type ProfitAndLoss,
  type TrialBalance,
} from '../api'
import { useAuth } from '../auth'
import { Card, ErrorNote, Loading, Note } from '../components/ui'

type Tab = 'trial-balance' | 'profit-and-loss' | 'balance-sheet'

function Rows({ rows, signed }: { rows: BalanceRow[]; signed?: boolean }) {
  if (rows.length === 0)
    return (
      <tr>
        <td colSpan={3} className="faint">
          nothing posted
        </td>
      </tr>
    )
  return (
    <>
      {rows.map((r) => (
        <tr key={r.account_id}>
          <td className="mono">{r.code}</td>
          <td>{r.name}</td>
          <td className="num">
            {signed ? formatInr(Math.abs(r.signed_paise)) : formatInr(r.debit_paise)}
          </td>
          {!signed && <td className="num">{formatInr(r.credit_paise)}</td>}
        </tr>
      ))}
    </>
  )
}

export default function Reports() {
  const today = new Date().toISOString().slice(0, 10)
  const [tab, setTab] = useState<Tab>('trial-balance')
  const [asAt, setAsAt] = useState(today)
  const [fyCode, setFyCode] = useState(() => {
    const now = new Date()
    const start = now.getMonth() + 1 >= 4 ? now.getFullYear() : now.getFullYear() - 1
    return `${start}-${String((start + 1) % 100).padStart(2, '0')}`
  })
  const [locationId, setLocationId] = useState('')

  const locations = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.get<Location[]>('/org/locations'),
  })
  const q = locationId ? `&location_id=${locationId}` : ''

  const tb = useQuery({
    queryKey: ['tb', asAt, locationId],
    queryFn: () => api.get<TrialBalance>(`/reports/trial-balance?as_at=${asAt}${q}`),
    enabled: tab === 'trial-balance',
  })
  const pl = useQuery({
    queryKey: ['pl', fyCode, locationId],
    queryFn: () => api.get<ProfitAndLoss>(`/reports/profit-and-loss?fy_code=${fyCode}${q}`),
    enabled: tab === 'profit-and-loss',
  })
  const bs = useQuery({
    queryKey: ['bs', asAt, locationId],
    queryFn: () => api.get<BalanceSheet>(`/reports/balance-sheet?as_at=${asAt}${q}`),
    enabled: tab === 'balance-sheet',
  })

  const { can } = useAuth()
  const fyFrom = `${fyCode.slice(0, 4)}-04-01`
  const fyTo = `${Number(fyCode.slice(0, 4)) + 1}-03-31`
  //: The same statement from the report pack, as an Excel file.
  const download = `/api/v1/reports/pack/${tab}/xlsx?${new URLSearchParams({
    ...(tab === 'profit-and-loss' ? { since: fyFrom, upto: fyTo } : { as_at: asAt }),
    ...(locationId ? { location_id: locationId } : {}),
  })}`

  const scope = locationId
    ? locations.data?.find((l) => l.id === locationId)?.name ?? 'one location'
    : 'the whole company'

  return (
    <>
      <Card
        title="Report"
        actions={
          can('report.any.export') && (
            <a className="btn" href={download}>
              Download Excel
            </a>
          )
        }
      >
        <div className="row">
          <div>
            <span className="caption">Statement</span>
            <select value={tab} onChange={(e) => setTab(e.target.value as Tab)}>
              <option value="trial-balance">Trial balance</option>
              <option value="profit-and-loss">Profit &amp; loss</option>
              <option value="balance-sheet">Balance sheet</option>
            </select>
          </div>
          {tab === 'profit-and-loss' ? (
            <div>
              <span className="caption">Financial year</span>
              <input value={fyCode} onChange={(e) => setFyCode(e.target.value)} />
            </div>
          ) : (
            <div>
              <span className="caption">As at</span>
              <input type="date" value={asAt} onChange={(e) => setAsAt(e.target.value)} />
            </div>
          )}
          <div>
            <span className="caption">Location</span>
            <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
              <option value="">All locations</option>
              {(locations.data ?? []).map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        </div>
        <p className="muted" style={{ marginBottom: 0 }}>
          Showing {scope}. Clinic figures come from the same journal lines as the company's,
          so they cannot disagree with them.
        </p>
      </Card>

      {tab === 'trial-balance' && (
        <>
          <ErrorNote error={tb.error} />
          {tb.isLoading ? (
            <Loading />
          ) : (
            <Card title={`Trial balance at ${tb.data?.as_at}`} tight>
              {tb.data && !tb.data.balanced && (
                <div style={{ padding: 16 }}>
                  <Note kind="error">
                    Debits and credits differ by {formatInr(tb.data.difference_paise)}. The books
                    are internally inconsistent — this is a defect, not a closing decision.
                  </Note>
                </div>
              )}
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account</th>
                    <th className="num">Debit</th>
                    <th className="num">Credit</th>
                  </tr>
                </thead>
                <tbody>
                  <Rows rows={tb.data?.rows ?? []} />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>Total</td>
                    <td className="num">{formatInr(tb.data?.total_debit_paise ?? 0)}</td>
                    <td className="num">{formatInr(tb.data?.total_credit_paise ?? 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </Card>
          )}
        </>
      )}

      {tab === 'profit-and-loss' && (
        <>
          <ErrorNote error={pl.error} />
          {pl.isLoading ? (
            <Loading />
          ) : (
            <Card
              title={`Profit & loss — ${pl.data?.fy_code}`}
              hint={`${pl.data?.since} to ${pl.data?.upto}`}
              tight
            >
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={3} style={{ fontWeight: 600, background: '#fafbfc' }}>
                      Income
                    </td>
                  </tr>
                  <Rows rows={pl.data?.income ?? []} signed />
                  <tr>
                    <td colSpan={3} style={{ fontWeight: 600, background: '#fafbfc' }}>
                      Expenses
                    </td>
                  </tr>
                  <Rows rows={pl.data?.expenses ?? []} signed />
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      {(pl.data?.profit_paise ?? 0) >= 0 ? 'Profit' : 'Loss'} for the year
                    </td>
                    <td className="num">{formatInr(pl.data?.profit_paise ?? 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </Card>
          )}
        </>
      )}

      {tab === 'balance-sheet' && (
        <>
          <ErrorNote error={bs.error} />
          {bs.isLoading ? (
            <Loading />
          ) : (
            <Card title={`Balance sheet at ${bs.data?.as_at}`} tight>
              <table>
                <thead>
                  <tr>
                    <th>Code</th>
                    <th>Account</th>
                    <th className="num">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={3} style={{ fontWeight: 600, background: '#fafbfc' }}>
                      Assets — {formatInr(bs.data?.total_assets_paise ?? 0)}
                    </td>
                  </tr>
                  <Rows rows={bs.data?.assets ?? []} signed />
                  <tr>
                    <td colSpan={3} style={{ fontWeight: 600, background: '#fafbfc' }}>
                      Liabilities — {formatInr(bs.data?.total_liabilities_paise ?? 0)}
                    </td>
                  </tr>
                  <Rows rows={bs.data?.liabilities ?? []} signed />
                  <tr>
                    <td colSpan={3} style={{ fontWeight: 600, background: '#fafbfc' }}>
                      Equity — {formatInr(bs.data?.total_equity_paise ?? 0)}
                    </td>
                  </tr>
                  <Rows rows={bs.data?.equity ?? []} signed />
                  <tr>
                    <td className="faint">—</td>
                    <td>
                      Retained earnings
                      <div className="faint">
                        derived, not an account — no closing entry is ever posted
                      </div>
                    </td>
                    <td className="num">{formatInr(bs.data?.retained_earnings_paise ?? 0)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={2}>
                      {bs.data?.balanced
                        ? 'Assets = liabilities + equity'
                        : `Out by ${formatInr(bs.data?.difference_paise ?? 0)}`}
                    </td>
                    <td className="num">{formatInr(bs.data?.total_assets_paise ?? 0)}</td>
                  </tr>
                </tfoot>
              </table>
            </Card>
          )}
        </>
      )}
    </>
  )
}
